import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";

const MAILTO =
  "mailto:muenchen@wagenhofer.capital?subject=Kontaktanfrage%20%E2%80%93%20Wagenhofer%20Capital";

export default function Kontakt() {
  useRevealOnScroll();

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="Kontakt | Wagenhofer Capital München"
        description="Vertrauliche Kontaktaufnahme zu Wagenhofer Capital – für Geschäftspartner, Inhaber, Projektentwickler und Vermieter. Antwort in der Regel innerhalb weniger Werktage."
        path="/kontakt"
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "Kontakt", to: "/kontakt" }]} />

      <PageHeader
        testId="page-contact"
        overline="Kontakt"
        title="Sprechen wir."
        subtitle="Für Geschäftspartner, Inhaber, Projektentwickler, Vermieter, Unternehmer und potenzielle Beteiligungen stehen wir für vertrauliche Gespräche zur Verfügung."
      />

      <section
        data-testid="section-contact"
        className="section-y border-b border-white/5"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* LEFT COLUMN: contact details */}
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-6">Direkter Kontakt</div>
            <div className="space-y-10">
              <div>
                <div className="flex items-center gap-2 overline mb-3">
                  <MapPin
                    className="w-3.5 h-3.5 text-[#D4AF37]"
                    strokeWidth={1.5}
                  />
                  Adresse
                </div>
                <address className="not-italic text-zinc-300 leading-relaxed">
                  Wagenhofer Capital GmbH
                  <br />
                  Max-Löw-Straße 27
                  <br />
                  85579 Neubiberg
                  <br />
                  Deutschland
                </address>
              </div>
              <div>
                <div className="flex items-center gap-2 overline mb-3">
                  <Mail
                    className="w-3.5 h-3.5 text-[#D4AF37]"
                    strokeWidth={1.5}
                  />
                  E-Mail
                </div>
                <a
                  href="mailto:muenchen@wagenhofer.capital"
                  data-testid="contact-email-link"
                  className="text-zinc-200 hover:text-[#D4AF37] transition-colors break-all"
                >
                  muenchen@wagenhofer.capital
                </a>
              </div>
              <div>
                <div className="flex items-center gap-2 overline mb-3">
                  <Clock
                    className="w-3.5 h-3.5 text-[#D4AF37]"
                    strokeWidth={1.5}
                  />
                  Antwortzeit
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed max-w-sm">
                  Anfragen werden vertraulich behandelt. Wir antworten in der
                  Regel innerhalb weniger Werktage.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: mail CTA card */}
          <div className="lg:col-span-7 reveal delay-2">
            <div className="relative border border-white/10 bg-[#070707] p-10 md:p-14">
              <span className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

              <div className="overline mb-6">Nachricht senden</div>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl tracking-tight font-medium leading-[1.1] mb-6">
                Schreiben Sie uns
                <br />
                <span className="text-zinc-400">direkt per E-Mail.</span>
              </h2>
              <p className="text-zinc-300 text-base md:text-lg leading-relaxed font-light mb-10 max-w-xl">
                Senden Sie Ihre Anfrage bequem über Ihr eigenes
                E-Mail-Programm – schnell, direkt und ohne Umweg über ein
                Formular. Alle Anfragen werden strikt vertraulich behandelt.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <a
                  href={MAILTO}
                  data-testid="contact-mailto-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black hover:bg-[#c19c30] transition-colors duration-300 px-10 py-5 uppercase tracking-[0.28em] text-[11px] font-semibold"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.75} />
                  E-Mail schreiben
                  <ArrowUpRight
                    className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    strokeWidth={1.5}
                  />
                </a>
                <div className="text-xs text-zinc-500 leading-relaxed max-w-xs">
                  Öffnet Ihr Standard-E-Mail-Programm mit vorausgefüllter
                  Empfängeradresse.
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <div className="overline mb-3 text-[#D4AF37]/80">
                  Empfänger
                </div>
                <a
                  href="mailto:muenchen@wagenhofer.capital"
                  className="font-display text-lg md:text-xl text-white hover:text-[#D4AF37] transition-colors break-all"
                >
                  muenchen@wagenhofer.capital
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAP */}
      <section data-testid="section-map" className="border-b border-white/5">
        <div className="relative aspect-[21/9] md:aspect-[21/7] bg-[#070707]">
          <iframe
            title="Standort Wagenhofer Capital – Max-Löw-Straße 27, Neubiberg bei München"
            aria-label="Karte mit Standort Max-Löw-Straße 27, 85579 Neubiberg"
            src="https://www.google.com/maps?q=Max-L%C3%B6w-Stra%C3%9Fe+27,+85579+Neubiberg&output=embed"
            className="absolute inset-0 w-full h-full grayscale contrast-110 brightness-75"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}
